<template>
  <div v-if="isLoaded">
    <DeleteModal
      :is-delete-modal-active.sync="isDeleteModalActive"
      :items="deleteItem"
      @confirm-delete="confirmDelete($event)" />
    <ContactTypeAdd
      ref="contactTypeAdd"
      :is-sidebar-active.sync="isSidebarActive"
      @add-contact-type="addContactType($event)" />
    <ContactTypeDetail
      :is-sidebar-active.sync="isSidebarDetailActive"
      :initial-contact-type="initialContactType"
      :selected-item.sync="selectedItem"
      @delete-item="openDeleteModal($event)"
      @edit-contact-type="editContactType($event)" />
    <ContactTypeFilter
      :search.sync="filter.searchCodeAndName"
      :status-filter.sync="filter.status"
      @fetch-filter="fetchFilter()" />
    <b-card class="mt-2">
      <TableTop
        v-model="pagination.limit"
        label="Add Contact Type"
        @active-sidebar="activeSidebar()"
        @change="fetchFilter()" />
      <b-table
        :items="items"
        :fields="fields"
        striped
        hover
        responsive
        @sort-changed="sortFilter($event)"
        @row-clicked="rowClick($event)">
        <template #cell(code)="data">
          <span class="font-weight-semibold text-primary">#{{ data.value }}</span>
        </template>
        <template #cell(status)="data">
          <b-badge :variant="$statusValue(data.value)">
            {{ data.value | formatStatusData }}
          </b-badge>
        </template>
        <template #cell(name)="data">
          <span class="font-weight-semibold">{{ data.value }}</span>
        </template>
        <template #cell(remark)="data">
          <div v-if="data && data.item && data.item.remark">
            {{ data.item.remark }}
          </div>
          <div v-else>
            -
          </div>
        </template>
      </b-table>
      <b-row>
        <b-col
          cols="12"
          sm="12"
          class="d-flex align-items-center justify-content-center justify-content-sm-end">
          <b-pagination
            v-model="pagination.page"
            :total-rows="pagination.totalDocs"
            :per-page="pagination.limit"
            first-number
            last-number
            class="mb-1 mt-2 mr-1"
            prev-class="prev-item"
            next-class="next-item"
            @change="showCurrentPage($event)">
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18" />
            </template>
            <template #next-text>
              <feather-icon
                icon="ChevronRightIcon"
                size="18" />
            </template>
          </b-pagination>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import TableTop from '@/components/TableTop.vue'
import ContactTypeAdd from '@/components/New/ContactType/ContactTypeAdd.vue'
import ContactTypeProvider from '@/resources/ContactTypeProvider'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ContactTypeDetail from '@/components/New/ContactType/ContactTypeDetail.vue'
import DeleteModal from '@/components/DeleteModal.vue'
import ContactTypeFilter from '../../components/New/ContactType/ContactTypeFilter.vue'

const ContactTypeService = new ContactTypeProvider()

export default {
  components: {
    ContactTypeAdd,
    ContactTypeFilter,
    TableTop,
    ContactTypeDetail,
    DeleteModal
  },
  data () {
    return {
      isLoaded: false,
      isSidebarActive: false,
      isSidebarDetailActive: false,
      isDeleteModalActive: false,
      filter: {
        searchCodeAndName: '',
        status: ''
      },
      pagination: {
        page: 1,
        limit: 10,
        totalDocs: 0
      },
      fields: [
        { key: 'code', label: 'Code', sortable: true },
        { key: 'name', label: 'Name', sortable: true },
        { key: 'remark', label: 'Remark' },
        { key: 'status', label: 'Status' }
      ],
      items: [],
      initialContactType: {},
      selectedItem: {},
      deleteItem: {}
    }
  },
  async created () {
    try {
      await this.getContactType()
    } finally {
      this.isLoaded = true
    }
  },
  methods: {
    fetchFilter () {
      this.setPagination(1)
      this.getContactType()
    },
    openDeleteModal (val) {
      this.deleteItem = { ...val }
      this.isDeleteModalActive = true
    },
    activeSidebar () {
      this.isSidebarActive = true
    },
    async getContactType () {
      try {
        const page = this.$route?.query?.page || this.pagination?.page || 1
        const limit = this.$route?.query?.limit || this.pagination?.limit || 10
        const data = await ContactTypeService.paginate(
          page, limit,
          this.filter
        )
        this.setPagination(data.page, data.totalDocs)
        this.items = [...data.data]
      } catch (err) {
        console.log(err)
      }
    },
    async addContactType (val) {
      try {
        await ContactTypeService.create(val)
        await this.getContactType()
        this.resetFormAndToggleSideBar(this.$refs.contactTypeAdd)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Add Record Successfully',
            icon: 'EditIcon',
            variant: 'success',
            text: '👋 Contact Type has been added'
          }
        })
      } catch (err) {
        console.error(err)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Add Fail',
            icon: 'EditIcon',
            variant: 'danger',
            text: '😞 Contact Type has been Failed'
          }
        })
      }
    },
    async editContactType (val) {
      try {
        const payload = { ...val }
        delete payload.id
        const { data } = await ContactTypeService.update(val.id, payload)
        await this.getContactType()
        this.setInitialContactTypeAndSelectItem(data)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Updated Successfully',
            icon: 'EditIcon',
            variant: 'success',
            text: '👋 Contact Type has been updated'
          }
        })
      } catch (err) {
        console.error(err)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Add Fail',
            icon: 'EditIcon',
            variant: 'danger',
            text: '😞 Contact Type has been Failed'
          }
        })
      }
    },
    async confirmDelete (val) {
      try {
        this.isSidebarDetailActive = false
        await ContactTypeService.softDelete(val.ID)
        await this.getContactType()
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Deleted Successfully',
            icon: 'EditIcon',
            variant: 'success',
            text: '👋 Contact Type has been deleted'
          }
        })
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Deleted Fail',
            icon: 'EditIcon',
            variant: 'danger',
            text: `😞 Contact Type has been Failed ${error}`
          }
        })
      }
    },
    rowClick (val) {
      this.setInitialContactTypeAndSelectItem(val)
      this.isSidebarDetailActive = true
    },
    setInitialContactTypeAndSelectItem (val) {
      this.selectedItem = { ...val }
      this.initialContactType = { ...val }
    },
    sortFilter (ctx) {
      console.log(ctx)
    },
    showCurrentPage (page) {
      this.setPagination(page)
      this.getContactType()
    },
    setPagination (page, totalDocs) {
      this.$router.replace(`contact-type?page=${page}`)
      this.pagination.totalDocs = totalDocs || this.pagination.totalDocs
      this.pagination.page = page || this.pagination.page
    },
    resetFormAndToggleSideBar (ref) {
      this.isSidebarActive = !this.isSidebarActive
      if (ref && ref.resetForm()) ref.resetForm()
    }
  }

}
</script>

<style lang="scss" scoped>
.card-body {
  padding: 0;
}
</style>
